/* eslint-disable no-console */
/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useReactiveVar, useQuery } from '@apollo/client';
import { navigate } from '@gatsbyjs/reach-router';
import { Box, Grid, useMediaQuery } from '@material-ui/core';
import { useGlobal, PageSection, Typography, PaymentFooter } from '@rcg/ui-react';
import { formatPrice } from '@rcg/pricing-format';
import InfoIcon from '@material-ui/icons/Info';
import { cookieCountry } from '../../utils/globalization/marketCookies';
import { globalizationPath } from '../../utils/globalization/globalizationPath';
import ItineraryDetails from '../../components/ItineraryDetails';
import useDictionary from '../../hooks/useDictionary';
import { ReactComponent as WarningIcon } from '../../assets/icons/warning-icon.svg';
import ProgressLineItems from '../../components/ProgressLine';
import GiftCards from '../../components/GiftCards';
import { heldConfirmInputMap, tokenizeCardAndBookFlight } from '../../utils/payment';
import setFilterPanel from '../../operations/localState/setFilterPanel';
import {
  APPLICATION_NAME,
  NAVIGATION_PATHS,
  PAYMENT_ERROR,
  PAYMENT_REVERSAL_ERROR,
  TRAVEL_AGENT,
  TRAVEL_AGENT_RESERVE,
  BOOKING_COUNTRY,
  SESSION_STORAGE_CHANGE_FLIGHTS,
  SESSION_STORAGE_CURRENT_BOOKING,
  SESSION_STORAGE_FLIGHT_TYPE_CODE,
  FLIGHT_TYPE_CODES,
  SESSION_STORAGE_BOOKING_PROFILE_NAME,
  SESSION_STORAGE_GDS_LOCATOR,
} from '../../utils/constants';
import useBookFlight from '../../operations/dataAccess/useBookFlight';
import useBookFlightAuthorization from '../../operations/dataAccess/useBookFlightAuthorization';
import Theme from '../../utils/theme';
import Dictionary from '../../utils/dictionary';
import useStyles from './index.style';
import ReviewCard from '../../components/ReviewCard';
import { TermsAndConditionsModal } from '../../components/CheckoutCard';
import PassengersDetailsWizard from '../../components/PassengerDetailsWizard';
import AirLoader from '../../components/AirLoader';
import {
  heldReservationVar,
  selectedReturnVar,
  selectedDepartureVar,
  bookingProfileVar,
  searchParamsVar,
  globalDataVar,
} from '../../operations/localState/reactiveVariables';
import {
  ENABLE_3DS_PAYMENT_GATEWAY,
  ENABLE_GIFT_CARD_TOKENIZATION,
  ENABLE_NEW_PAYMENT_LAYOUT,
  ENABLE_GIFT_CARDS_PAYMENT,
  ENABLE_UNIFIED_PAYMENT,
  LDUser,
} from '../../utils/ldUser';
import ExtendSessionModal from '../../components/ExtendSessionModal';
import { LAUNCH_DARKLY_FLAGS_AIR } from '../../operations/queries';
import useGuestAirProfile from '../../operations/dataAccess/useGuestAirProfile';
import CreditCardInformation from '../../components/CreditCardInformation';
import setBookedReservation from '../../operations/localState/setBookedReservation';
import AirFooter from '../../components/AirFooter';
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from '../../operations/sessionStorage/utils';
import getBrand from '../../utils/getBrand';
import NotificationBannerComponent from '../../components/NotificationBanner';
import useCommonStyles from '../../utils/useCommonStyles';

const currentBrand = getBrand();
const getConfirmInputMap = () =>
  heldReservationVar()?.holdFlight
    ? heldConfirmInputMap(bookingProfileVar, heldReservationVar, searchParamsVar)
    : {};
const checkGraphqlErrors = (error) => error && Array.isArray(error.graphQLErrors);
const paymentFlightInfo = (heldReservationPayment, selectedDeparture, selectedReturn) => {
  const { holdFlight } = heldReservationPayment || {};
  const { outboundFlight } = holdFlight || {};
  let { inboundFlight } = holdFlight || {};
  if (!inboundFlight) {
    inboundFlight = outboundFlight;
  }

  const extraDataPayment = {
    departureFlight: selectedDeparture,
    returnFlight: selectedReturn || selectedDeparture,
  };
  const { currency } = extraDataPayment.returnFlight?.cabin?.totalPrice || 'USD';

  return {
    holdFlight,
    inboundFlight,
    outboundFlight,
    extraDataPayment,
    currency,
  };
};
const cardErrorDescriptionGenerator = (cardErrorDescription, reversalErrorDescription) =>
  `${cardErrorDescription}${cardErrorDescription ? ' ' : ''}${reversalErrorDescription}`;
const gifCardBoxRender = (giftCards, classes, renderContent) => {
  const giftCardBox = [];
  if (giftCards.length > 0) {
    giftCards.forEach((giftCard) => {
      const boxGiftcard = (
        <Box className={classes.tooltipRow}>
          <span>
            {renderContent(
              Dictionary.airPaymentFooterAmountGiftCard.key,
              Dictionary.airPaymentFooterAmountGiftCard.defaultValue,
            )}
            {giftCard.info.cardNumber.substr(-4)}
          </span>
          <span>{giftCard.charged}</span>
        </Box>
      );
      giftCardBox.push(boxGiftcard);
    });
  }
  return giftCardBox;
};

const getEnableData = (termsChecked, creditCardFormEnable, disableCreditCardForm, isRefund) => {
  return (
    (termsChecked && creditCardFormEnable) ||
    (termsChecked && disableCreditCardForm) ||
    (termsChecked && isRefund)
  );
};
const getAmountFormatValueData = (holdFlight, currency, priceEnableChangeFlight) => {
  return holdFlight.priceChange
    ? formatPrice(holdFlight.priceChange.newTotal.value, currency, true, true, true)
    : priceEnableChangeFlight();
};

const checkHeldReservationPayment = (heldReservationPayment) => {
  return (
    !heldReservationPayment ||
    !heldReservationPayment.holdFlight ||
    (!heldReservationPayment.holdFlight.outboundFlight &&
      !heldReservationPayment.holdFlight.inboundFlight)
  );
};

const getFlightInfo = (
  outboundFlight,
  inboundFlight,
  isSm,
  shouldDisplayBanner,
  cancelPayway,
  errorValidation,
  giftCardErrorBanner,
) => {
  const paymentOutboundFlightFirstSegment = outboundFlight.flightSegments[0];
  const paymentOutboundFlightSegmentsCount = outboundFlight.flightSegments.length - 1;
  const paymentOutboundFlightLastSegment =
    outboundFlight.flightSegments[paymentOutboundFlightSegmentsCount];
  const paymentOutboundFlightDepartureCity =
    paymentOutboundFlightFirstSegment.departure.airport.code;
  const paymentOutboundFlightArrivalCity = paymentOutboundFlightLastSegment.arrival.airport.code;

  const paymentInboundFlightFirstSegment = inboundFlight.flightSegments[0];
  const paymentInboundFlightSegmentsCount = inboundFlight.flightSegments.length - 1;
  const paymentInboundFlightLastSegment =
    inboundFlight.flightSegments[paymentInboundFlightSegmentsCount];
  const paymentInboundFlightDepartureCity = paymentInboundFlightFirstSegment.departure.airport.code;
  const paymentInboundFlightArrivalCity = paymentInboundFlightLastSegment.arrival.airport.code;

  const displayMobileBanner =
    isSm && (shouldDisplayBanner || cancelPayway || errorValidation || giftCardErrorBanner);
  const displayDesktopBanner =
    !isSm && (shouldDisplayBanner || cancelPayway || errorValidation || giftCardErrorBanner);

  return {
    paymentOutboundFlightFirstSegment,
    paymentOutboundFlightSegmentsCount,
    paymentOutboundFlightLastSegment,
    paymentOutboundFlightDepartureCity,
    paymentInboundFlightFirstSegment,
    paymentOutboundFlightArrivalCity,
    paymentInboundFlightSegmentsCount,
    paymentInboundFlightLastSegment,
    paymentInboundFlightDepartureCity,
    paymentInboundFlightArrivalCity,
    displayMobileBanner,
    displayDesktopBanner,
  };
};
const showBannerInformation = (
  setCancelPayway,
  setErrorValidation,
  setGiftCardErrorBanner,
  setShouldDisplayBanner,
  setShouldDisplayBannerInfo,
  setGiftCardErrorBannerInfo,
) => {
  const showPaymentErrorBanner = (title, description) => {
    setCancelPayway(false);
    setErrorValidation(false);
    setGiftCardErrorBanner(false);
    setShouldDisplayBanner(true);
    setShouldDisplayBannerInfo({ title, description });
    document.querySelector('.main-container').scrollTo(0, 0);
  };

  const showCancelPaywayBanner = () => {
    setShouldDisplayBanner(false);
    setErrorValidation(false);
    setGiftCardErrorBanner(false);
    setCancelPayway(true);
    document.querySelector('.main-container').scrollTo(0, 0);
  };

  const showErrorvalidationBanner = () => {
    setShouldDisplayBanner(false);
    setCancelPayway(false);
    setGiftCardErrorBanner(false);
    setErrorValidation(true);
    document.querySelector('.main-container').scrollTo(0, 0);
  };

  const showErrorGiftCardsBanner = (title, description) => {
    setShouldDisplayBanner(false);
    setCancelPayway(false);
    setErrorValidation(false);
    setGiftCardErrorBanner(true);
    setGiftCardErrorBannerInfo({ title, description });
    document.querySelector('.main-container').scrollTo(0, 0);
  };

  const hideErrorBanners = () => {
    setShouldDisplayBanner(false);
    setCancelPayway(false);
    setErrorValidation(false);
    setGiftCardErrorBanner(false);
  };
  return {
    showPaymentErrorBanner,
    showCancelPaywayBanner,
    showErrorvalidationBanner,
    showErrorGiftCardsBanner,
    hideErrorBanners,
  };
};

const isValidBoolean = (variable) => {
  return typeof variable !== 'undefined' && typeof variable === 'boolean';
};

const Payment = () => {
  const classes = useStyles();
  const getDictionaryValue = useDictionary(Dictionary);
  const styles = useCommonStyles();
  const {
    data: { lang, market },
    renderContent,
  } = useGlobal();
  const launchDarklyParameters = getSessionStorageItem(BOOKING_COUNTRY) || {
    country: 'USA',
    officeID: 'mia',
  };
  const isChangeFlights = getSessionStorageItem(SESSION_STORAGE_CHANGE_FLIGHTS);
  const { data: launchDarklyFlags, loading: launchDarklyLoading } = useQuery(
    LAUNCH_DARKLY_FLAGS_AIR,
    {
      variables: {
        ldUser: LDUser(),
        language: lang,
        bookingCountry: launchDarklyParameters?.country,
        clientApp: APPLICATION_NAME,
        application: APPLICATION_NAME,
        officeID: launchDarklyParameters?.officeID,
        brand: currentBrand?.ldBrand,
        country: cookieCountry().toUpperCase(),
      },
    },
  );

  const { REACT_APP_AIR_3DS_ENV } = process.env;
  const royalPayway = useRef(null);
  const passengerDetailsFormRef = useRef(null);
  const paymentMethodFormRef = useRef(null);
  const urlPath = globalizationPath(market, lang, NAVIGATION_PATHS.base);
  const heldReservationPayment = useReactiveVar(heldReservationVar);
  const selectedDeparture = useReactiveVar(selectedDepartureVar);
  const selectedReturn = useReactiveVar(selectedReturnVar);
  const bookingProfile = useReactiveVar(bookingProfileVar);
  const isSm = useMediaQuery(Theme.breakpoints.down('sm'));
  const [shouldDisplayBanner, setShouldDisplayBanner] = useState(false);
  const [shouldDisplayBannerInfo, setShouldDisplayBannerInfo] = useState({});
  const [paymentSelect, setPaymentSelect] = useState('reserve');
  const [fullPaymentLoading, setFullPaymentLoading] = useState(false);
  const [isGiftcardLoading, setIsGiftcardLoading] = useState(false);
  const [cancelPayway, setCancelPayway] = useState(false);
  const [creditCardFormEnable, setCreditCardFormEnable] = useState(false);
  const [errorValidation, setErrorValidation] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [giftCardErrorBanner, setGiftCardErrorBanner] = useState(false);
  const [remainingBalance, setRemainingBalance] = useState(false);
  const [giftCards, setGiftCard] = useState([]);
  const [creditcard, setCreditCard] = useState({});
  const [giftCardErrorBannerInfo, setGiftCardErrorBannerInfo] = useState({
    title: '',
    description: '',
  });
  const [ldLoader, setLdLoader] = useState(true);
  const [disableCreditCardForm, setDisableCreditCardForm] = useState(false);
  const [submitPaymentRef, setSubmitPaymentRef] = React.useState(null);
  const [openTermsAndConditionsModal, setOpenTermsAndConditionsModal] = useState(false);
  const [ldAirFlagsThreeDS, setLdAirFlagsThreeDS] = useState();
  const [ldAirFlagsThreeDSGiftCard, setLdAirFlagsThreeDSGiftCard] = useState();
  const [ldAirFlagsNewPayment, setLdAirFlagsNewPayment] = useState();
  const [ldAirFlagsGiftCard, setLdAirFlagsGiftCard] = useState();
  const [ldAirFlagsUnifiedPayment, setLdAirFlagsUnifiedPayment] = useState();
  const currentBooking = getSessionStorageItem(SESSION_STORAGE_CURRENT_BOOKING);
  const currentBookingType = currentBooking?.type;

  const [totalBalance, setTotalBalance] = useState(0.0);
  const [newExtraDataPayment, setNewExtraDataPayment] = useState(undefined);
  const [isRefund, setIsRefund] = useState(false);
  const [expandReviewCard, setExpandReviewCard] = useState(false);

  const [passengerInfoComplete, setpassengerInfoComplete] = useState(false);
  const [showLineError, setShowLineError] = useState(false);
  const [lineErrorMessage, setLineErrorMessage] = useState(
    getDictionaryValue(Dictionary.airCheckoutPaymentFooterErrorLabel.key),
  );
  const [firstValueCheckbox, setFirstValueCheckbox] = useState(true);
  const [payLaterFromAmend, setPayLaterFromAmend] = useState(false);
  const flightTypeCodeFromSession = getSessionStorageItem(SESSION_STORAGE_FLIGHT_TYPE_CODE);
  const isOneWay = flightTypeCodeFromSession === FLIGHT_TYPE_CODES.ONE_WAY;

  let heldConfirmInputInformation = getConfirmInputMap();
  const {
    showPaymentErrorBanner,
    showCancelPaywayBanner,
    showErrorvalidationBanner,
    showErrorGiftCardsBanner,
    hideErrorBanners,
  } = showBannerInformation(
    setCancelPayway,
    setErrorValidation,
    setGiftCardErrorBanner,
    setShouldDisplayBanner,
    setShouldDisplayBannerInfo,
    setGiftCardErrorBannerInfo,
  );

  const setupThreeDSComponent = async () => {
    const threeDsComponentElement = royalPayway.current;
    // call setup to initiate the flow. If the paywayThreeDsParam exists in the URL,
    // that means the challenge flow has been completed and we're supposed to retrieve the
    // transaction instead of creating a new session.
    await threeDsComponentElement.setup();
  };
  const getCardsFound = (error) => {
    const cardErrs = error.graphQLErrors.find(
      ({ extensions }) =>
        extensions.code === PAYMENT_ERROR || extensions.code === PAYMENT_REVERSAL_ERROR,
    );
    return cardErrs?.extensions?.errors;
  };

  const [guestAirProfile, { data: guestAirProfileData, error: guestAirProfileError }] =
    useGuestAirProfile({ fetchPolicy: 'network-only' });
  const [bookFlightPayment, { loading: bookFlightLoading }] = useBookFlight({
    onCompleted: (data) => {
      setBookedReservation(data);
      setFullPaymentLoading(false);
      navigate(`${urlPath}${NAVIGATION_PATHS.confirmation}`);
    },
    onError: (error) => {
      setFullPaymentLoading(false);
      if (ldAirFlagsUnifiedPayment) {
        const sessionStorageBooking = sessionStorage.getItem(SESSION_STORAGE_BOOKING_PROFILE_NAME);
        const locators = sessionStorage.getItem(SESSION_STORAGE_GDS_LOCATOR);
        const changeFlightsStatus = sessionStorage.getItem(SESSION_STORAGE_CHANGE_FLIGHTS);
        const bookingInfoParse = JSON.parse(sessionStorageBooking);
        setTimeout(
          () =>
            guestAirProfile({
              variables: {
                input: {
                  bookingId: bookingInfoParse?.currentBooking?.id,
                  guestIds: heldConfirmInputInformation?.guests?.map((guest) => guest.id),
                  correlationID: heldConfirmInputInformation?.correlationID,
                  locators: changeFlightsStatus === 'true' ? locators : [],
                },
              },
            }),
          5000,
        );
      } else {
        setFullPaymentLoading(false);
        if (checkGraphqlErrors(error)) {
          const cardsFound = getCardsFound(error);
          const cardErrors = cardsFound?.filter((card) => card.error === PAYMENT_ERROR);
          const cardReversalErrors = cardsFound?.filter(
            (card) => card.error === PAYMENT_REVERSAL_ERROR,
          );

          if (cardsFound?.length > 0) {
            let cardErrorDescription = '';
            if (cardErrors.length === 1) {
              cardErrorDescription = renderContent(
                Dictionary.airErrorYourPaymentLabel.key,
                Dictionary.airErrorYourPaymentLabel.defaultValue,
                {
                  findReplace: [
                    {
                      find: '{cardNum}',
                      replace: cardErrors.map((cardErr) => cardErr.card),
                    },
                  ],
                },
              );
            } else if (cardErrors.length > 1) {
              cardErrorDescription = renderContent(
                Dictionary.airErrorYourPaymentsLabel.key,
                Dictionary.airErrorYourPaymentsLabel.defaultValue,
                {
                  findReplace: [
                    {
                      find: '{cardNums}',
                      replace: cardErrors.map((cardErr) => cardErr.card).join(', '),
                    },
                  ],
                },
              );
            }

            let reversalErrorDescription = '';
            if (cardReversalErrors.length === 1) {
              reversalErrorDescription = renderContent(
                Dictionary.airErrorPaymentAuthorizationReversal.key,
                Dictionary.airErrorPaymentAuthorizationReversal.defaultValue,
                {
                  findReplace: [
                    {
                      find: '{cardNum}',
                      replace: cardReversalErrors.map((cardErr) => cardErr.card),
                    },
                  ],
                },
              );
            } else if (cardReversalErrors.length > 1) {
              reversalErrorDescription = renderContent(
                Dictionary.airErrorPaymentAuthorizationReversals.key,
                Dictionary.airErrorPaymentAuthorizationReversals.defaultValue,
                {
                  findReplace: [
                    {
                      find: '{cardNums}',
                      replace: cardReversalErrors.map((cardErr) => cardErr.card).join(', '),
                    },
                  ],
                },
              );
            }

            showPaymentErrorBanner(
              renderContent(
                Dictionary.airPaymentFailedLabel.key,
                Dictionary.airPaymentFailedLabel.defaultValue,
              ),
              cardErrorDescriptionGenerator(cardErrorDescription, reversalErrorDescription),
            );
          } else {
            showPaymentErrorBanner(
              renderContent(
                Dictionary.airFlightBookFlightsReservationErrorTechnicalIssuesTitle.key,
                Dictionary.airFlightBookFlightsReservationErrorTechnicalIssuesTitle.defaultValue,
              ),
              renderContent(
                Dictionary.airFlightBookFlightsReservationErrorTechnicalIssuesDescription.key,
                Dictionary.airFlightBookFlightsReservationErrorTechnicalIssuesDescription
                  .defaultValue,
              ),
            );
          }
        } else {
          navigate(`${urlPath}${NAVIGATION_PATHS.error}`);
        }
      }
    },
  });

  const [bookFlightPaymentAuthorization, { loading: bookFlightAuthorizationLoading }] =
    useBookFlightAuthorization({
      onCompleted: (data) => {
        const { bookFlightAuthorization } = data;
        setBookedReservation({ bookFlight: bookFlightAuthorization });
        setFullPaymentLoading(false);
        navigate(`${urlPath}${NAVIGATION_PATHS.confirmation}`);
      },
      onError: () => {
        const changeFlightsStatus = sessionStorage.getItem(SESSION_STORAGE_CHANGE_FLIGHTS);
        const sessionStorageBooking = sessionStorage.getItem(SESSION_STORAGE_BOOKING_PROFILE_NAME);
        const bookingInfoParse = JSON.parse(sessionStorageBooking);
        const locators = sessionStorage.getItem(SESSION_STORAGE_GDS_LOCATOR);
        guestAirProfile({
          variables: {
            input: {
              bookingId: bookingInfoParse?.currentBooking?.id,
              guestIds: heldConfirmInputInformation?.guests?.map((guest) => guest.id),
              correlationID: heldConfirmInputInformation?.correlationID,
              locators: changeFlightsStatus === 'true' ? locators : [],
            },
          },
        });
      },
    });

  const { holdFlight, inboundFlight, outboundFlight, extraDataPayment, currency } =
    paymentFlightInfo(heldReservationPayment, selectedDeparture, selectedReturn);

  useEffect(() => {
    if (guestAirProfileData?.guestAirProfile?.hasFlights) {
      const { guestAirProfile: dataProfileGap } = guestAirProfileData;
      setFullPaymentLoading(false);
      setBookedReservation({ bookFlight: dataProfileGap });
      navigate(`${urlPath}${NAVIGATION_PATHS.confirmation}`);
    } else if (guestAirProfileData?.guestAirProfile?.hasFlights === false || guestAirProfileError) {
      showPaymentErrorBanner(
        getDictionaryValue(Dictionary.airFlightBookFlightsReservationErrorTechnicalIssuesTitle.key),
        getDictionaryValue(
          Dictionary.airFlightBookFlightsReservationErrorTechnicalIssuesDescription.key,
        ),
      );
      setFullPaymentLoading(false);
    }
  }, [guestAirProfileData, guestAirProfileError]);
  useEffect(() => {
    if (launchDarklyFlags) {
      setLdAirFlagsThreeDS(launchDarklyFlags.ldFlags[ENABLE_3DS_PAYMENT_GATEWAY]);
      setLdAirFlagsThreeDSGiftCard(launchDarklyFlags.ldFlags[ENABLE_GIFT_CARD_TOKENIZATION]);
      setLdAirFlagsNewPayment(launchDarklyFlags.ldFlags[ENABLE_NEW_PAYMENT_LAYOUT]);
      setLdAirFlagsGiftCard(launchDarklyFlags.ldFlags[ENABLE_GIFT_CARDS_PAYMENT]);
      setLdAirFlagsUnifiedPayment(launchDarklyFlags.ldFlags[ENABLE_UNIFIED_PAYMENT]);
      setTimeout(() => {
        setLdLoader(false);
      }, 3000);
    }
  }, [launchDarklyFlags, launchDarklyLoading]);

  useEffect(() => {
    if (ldAirFlagsNewPayment) {
      setFilterPanel({ isOpen: true });
    }
    return () => {
      setFilterPanel({ isOpen: false });
    };
  }, [ldAirFlagsNewPayment]);

  useEffect(() => {
    document.querySelector('.main-container').scrollTo(0, 0);
    // True means 3ds component should be in the payment flow
    if (ldAirFlagsThreeDS || ldAirFlagsThreeDSGiftCard) {
      const setupPaywayInterval = setInterval(() => {
        if (royalPayway.current && royalPayway.current.setup) {
          // check if threeds script is ready
          clearInterval(setupPaywayInterval);
          setupThreeDSComponent();
        }
      }, 500);
    }
  }, [ldAirFlagsThreeDS, ldAirFlagsThreeDSGiftCard]);

  useEffect(() => {
    setNewExtraDataPayment({
      ...extraDataPayment,
      returnFlight: {
        ...extraDataPayment.returnFlight,
        cabin: {
          ...extraDataPayment.returnFlight?.cabin,
          totalPrice: {
            ...extraDataPayment.returnFlight?.cabin.totalPrice,
            value: Number(totalBalance.toFixed(2)),
          },
        },
      },
    });
  }, [totalBalance]);

  useEffect(() => {
    if ((payLaterFromAmend && termsChecked) || (creditCardFormEnable && termsChecked)) {
      setShowLineError(false);
    }
  }, [payLaterFromAmend, creditCardFormEnable]);

  if (checkHeldReservationPayment(heldReservationPayment)) {
    window.location.href = `${urlPath}${NAVIGATION_PATHS.resultsDeparture}`;
    return '';
  }

  const giftCardBox = gifCardBoxRender(giftCards, classes, renderContent);

  const checkboxHandler = (e) => {
    setTermsChecked(e.target.checked);
    if (e.target.checked) {
      setShowLineError(false);
    } else {
      setLineErrorMessage(getDictionaryValue(Dictionary.airCheckoutPaymentFooterErrorLabel.key));
      setShowLineError(true);
    }
    setFirstValueCheckbox(false);
  };

  const getBannerElement = () => {
    if (errorValidation) {
      return (
        <div className={`${styles.notification} ${classes.notificationBanner}`}>
          <NotificationBannerComponent
            title={renderContent(
              Dictionary.airPaymentErrorTitle.key,
              Dictionary.airPaymentErrorTitle.defaultValue,
            )}
            description={renderContent(
              Dictionary.airPaymentErrorMessage.key,
              Dictionary.airPaymentErrorMessage.defaultValue,
            )}
            type="error"
            renderCloseBtn
            isBannerOpen
            onClose={() => {
              setErrorValidation(false);
            }}
          />
        </div>
      );
    }
    if (cancelPayway) {
      return (
        <div className={`${styles.notification} ${classes.notificationBanner}`}>
          <NotificationBannerComponent
            title={renderContent(
              Dictionary.airCancellPaywayTitleBanner.key,
              Dictionary.airCancellPaywayTitleBanner.defaultValue,
            )}
            description={renderContent(
              Dictionary.airCancellPaywayDescriptionBanner.key,
              Dictionary.airCancellPaywayDescriptionBanner.defaultValue,
            )}
            type="error"
            renderCloseBtn
            isBannerOpen
            onClose={() => {
              setCancelPayway(false);
            }}
          />
        </div>
      );
    }
    if (giftCardErrorBanner) {
      return (
        <div className={`${styles.notification} ${classes.notificationBanner}`}>
          <NotificationBannerComponent
            title={giftCardErrorBannerInfo.title}
            description={giftCardErrorBannerInfo.description}
            type="error"
            renderCloseBtn
            isBannerOpen
            onClose={() => {
              setGiftCardErrorBanner(false);
            }}
          />
        </div>
      );
    }
    return (
      <div className={`${styles.notification} ${classes.notificationBanner}`}>
        <NotificationBannerComponent
          title={shouldDisplayBannerInfo.title}
          description={shouldDisplayBannerInfo.description}
          type="error"
          renderCloseBtn
          isBannerOpen
          onClose={() => {
            setGiftCardErrorBanner(false);
          }}
        />
      </div>
    );
  };

  const { displayMobileBanner, displayDesktopBanner } = getFlightInfo(
    outboundFlight,
    inboundFlight,
    isSm,
    shouldDisplayBanner,
    cancelPayway,
    errorValidation,
    giftCardErrorBanner,
  );

  const handleOnChangePayment = (event) => {
    setPaymentSelect(event);
    if (event === 'reserve') setCreditCardFormEnable(true);
  };

  const handleOnChangeCTA = (event) => {
    setCreditCardFormEnable(event);
  };
  const getGiftCardNumber = (giftcard) => giftcard.info.cardNumber.replace(/\s/g, '');

  const enable = getEnableData(termsChecked, creditCardFormEnable, disableCreditCardForm, isRefund);

  const handleSubmitPayment = async () => {
    if (enable) setFullPaymentLoading(true);

    if (ldAirFlagsUnifiedPayment && !payLaterFromAmend && !isRefund) {
      const {
        trigger: { makePayment },
      } = window.UnifiedPaymentComponent;

      if (passengerDetailsFormRef.current) {
        passengerDetailsFormRef.current.handleSubmit();

        if (!passengerInfoComplete) {
          setTimeout(() => {
            setFullPaymentLoading(false);
            const wrapperElem = document.querySelector('#wizard-wrapper');
            const stepButtonPrimaryPassenger = document.querySelector('#step-button-0');
            if (wrapperElem && stepButtonPrimaryPassenger) {
              stepButtonPrimaryPassenger.click();
              wrapperElem.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            }
            passengerDetailsFormRef.current.handleSubmit();
          }, 100);
        }
      }
      if (!enable) {
        if (termsChecked) {
          const paymentFormElement = document.querySelector(
            'react-unified-payment #full-width-tabpanel-0',
          );
          setLineErrorMessage(
            getDictionaryValue(Dictionary.airCheckoutPaymentFooterUPCFormErrorLabel.key),
          );
          paymentFormElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        } else if (!termsChecked) {
          setLineErrorMessage(
            getDictionaryValue(Dictionary.airCheckoutPaymentFooterErrorLabel.key),
          );
        }
        setFullPaymentLoading(false);
        setShowLineError(true);
      }
      if (enable && passengerInfoComplete) {
        heldConfirmInputInformation = getConfirmInputMap();
        setSessionStorageItem('emailInfo', heldConfirmInputInformation);
        makePayment();
      } else if (termsChecked && passengerInfoComplete) {
        makePayment();
      }
    } else {
      let creditCardError = true;
      if (firstValueCheckbox || !termsChecked) {
        setShowLineError(true);
      }
      if (paymentMethodFormRef.current) {
        if (
          paymentMethodFormRef.current.errors &&
          Object.keys(paymentMethodFormRef.current.errors).length === 0
        ) {
          creditCardError = true;
        } else {
          creditCardError = false;
        }
      }
      if (passengerDetailsFormRef.current) {
        passengerDetailsFormRef.current.handleSubmit();
        if (!passengerInfoComplete) {
          setTimeout(() => {
            setFullPaymentLoading(false);
            const wrapperElem = document.querySelector('#wizard-wrapper');
            const stepButtonPrimaryPassenger = document.querySelector('#step-button-0');
            if (wrapperElem && stepButtonPrimaryPassenger) {
              stepButtonPrimaryPassenger.click();
              wrapperElem.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            }
            passengerDetailsFormRef.current.handleSubmit();
          }, 100);
        }
      }

      if (enable && !showLineError && passengerInfoComplete && creditCardError) {
        if (paymentSelect === 'reserve') {
          if (ldAirFlagsUnifiedPayment) {
            bookFlightPaymentAuthorization({
              variables: { heldConfirmInput: heldConfirmInputInformation },
            });
          } else {
            bookFlightPayment({
              variables: { heldConfirmInput: heldConfirmInputInformation },
            });
          }

          if (
            bookingProfile.type === TRAVEL_AGENT ||
            currentBookingType === TRAVEL_AGENT ||
            bookingProfile.bookingType === TRAVEL_AGENT
          ) {
            setSessionStorageItem(TRAVEL_AGENT_RESERVE, true);
          }
        } else if (disableCreditCardForm) {
          const globalData = globalDataVar();
          const paymentInput = giftCards.map((giftcard) => {
            return {
              cardType: 'VA',
              cardNumber: giftcard.info.cardNumber.replace(/\s/g, ''),
              expiration: `${giftcard.info.expiryMonth < 10 ? '0' : ''}${
                giftcard.info.expiryMonth
              }${giftcard.info.expiryYear.slice(-2)}`,
              amount: giftcard.chargedNotFormatted,
              cvv: giftcard.info.securityCode,
              paymentMethod: 'GC',
            };
          });
          if (ldAirFlagsThreeDSGiftCard) {
            const giftCardsFor3ds = giftCards.map((giftcard) => {
              const giftCardNumber = getGiftCardNumber(giftcard);
              return {
                correlationId: giftCardNumber.substr(-4),
                order: {
                  bookingId: String(+bookingProfileVar().flightProfile.id),
                  currency: String(bookingProfile.flightProfile.currency),
                  country: globalData.country,
                },
                paymentMethod: {
                  cardNumber: giftCardNumber,
                  expirationYear: giftcard.info.expiryYear,
                  expirationMonth: String(giftcard.info.expiryMonth).padStart(2, '0'),
                },
              };
            });
            const threeDsComponentElement = royalPayway.current;
            tokenizeCardAndBookFlight(
              threeDsComponentElement,
              giftCardsFor3ds,
              paymentInput,
              bookFlightPayment,
              heldConfirmInputInformation,
              showErrorvalidationBanner,
            );
          } else {
            bookFlightPayment({
              variables: {
                heldConfirmInput: heldConfirmInputInformation,
                paymentInput,
              },
            });
          }
        } else {
          setFullPaymentLoading(false);
          submitPaymentRef.current.handleSubmit();
        }
      } else {
        setFullPaymentLoading(false);
      }
    }
  };

  const priceEnableChangeFlight = () => {
    if (isChangeFlights) {
      return formatPrice(Math.abs(totalBalance), currency, true, true, true);
    }
    return formatPrice(
      extraDataPayment.returnFlight.cabin.totalPrice.value,
      currency,
      true,
      true,
      true,
    );
  };

  const amountFormatValue = getAmountFormatValueData(holdFlight, currency, priceEnableChangeFlight);

  const getAmountOnCreditCard = () => {
    if (ldAirFlagsUnifiedPayment && !payLaterFromAmend && !isRefund) {
      return amountFormatValue;
    }
    if (paymentSelect === 'reserve') {
      return formatPrice(0, currency, true, true, true);
    }
    return amountFormatValue;
  };
  const tooltipContent =
    isRefund && totalBalance !== 0 ? (
      <Box className={classes.tooltipWrapper}>
        {renderContent(
          Dictionary.airCheckoutRefundTooltipContent.key,
          Dictionary.airCheckoutRefundTooltipContent.defaultValue,
        )}
      </Box>
    ) : (
      <Box className={classes.tooltipWrapper}>
        <Box className={classes.tootipTilte}>
          {renderContent(
            Dictionary.airPaymentFooterPaymentDetail.key,
            Dictionary.airPaymentFooterPaymentDetail.defaultValue,
          )}
        </Box>
        <Box>
          {giftCardBox}
          {!disableCreditCardForm && (
            <>
              <Box className={classes.tooltipRow}>
                <span>
                  {renderContent(
                    Dictionary.airPaymentFooterAmountCreditCard.key,
                    Dictionary.airPaymentFooterAmountCreditCard.defaultValue,
                  )}{' '}
                  {creditcard.cardNumber !== undefined && creditcard.cardNumber.length > 4
                    ? creditcard.cardNumber.substr(-4)
                    : ''}
                </span>
                <span>
                  {remainingBalance && giftCards.length > 0
                    ? formatPrice(remainingBalance, giftCards[0].currency, true, true, true)
                    : getAmountOnCreditCard()}
                </span>
              </Box>
            </>
          )}
          <Box className={classes.tooltipTotal}>
            <span>
              {renderContent(
                Dictionary.airPaymentFooterTotalPayToday.key,
                Dictionary.airPaymentFooterTotalPayToday.defaultValue,
              )}
            </span>
            <span>{getAmountOnCreditCard()}</span>
          </Box>
        </Box>
      </Box>
    );

  const tooltipLabel = (
    <Box className={classes.tooltipLabel}>
      <Typography className={classes.tooltipAmount}> {amountFormatValue} </Typography>
      <InfoIcon className={classes.infoIcon} color="primary" />
    </Box>
  );

  const paymentSummaryLabel = (
    <p className={classes.paymentSummaryLabel}>
      {isRefund && totalBalance !== 0
        ? renderContent(
            Dictionary.airFlightSummaryBalanceRefundTotalLabel.key,
            Dictionary.airFlightSummaryBalanceRefundTotalLabel.defaultValue,
          )
        : renderContent(
            Dictionary.airPaymentFooterSummaryLabel.key,
            Dictionary.airPaymentFooterSummaryLabel.defaultValue,
          )}
      <span>{isRefund && totalBalance !== 0 ? '' : getAmountOnCreditCard()}</span>
    </p>
  );

  const handleTermsAndConditionsModal = (event) => {
    event.preventDefault();
    setOpenTermsAndConditionsModal(true);
  };

  const handleCloseTermsAndConditionsModal = () => {
    setOpenTermsAndConditionsModal(false);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      setOpenTermsAndConditionsModal(true);
    }
  };

  const checkboxLabel = (
    <>
      <div className={classes.labelsStickyFooter}>
        <div className="labelCheckbox">
          <Typography>
            {renderContent(
              Dictionary.airPaymentFooterCheckboxLabel.key,
              Dictionary.airPaymentFooterCheckboxLabel.defaultValue,
            )}
            <span> </span>
            <span
              data-testid="checkbox-open-modal-element"
              className={classes.checkboxOpenModalButton}
              tabIndex={0}
              role="button"
              onClick={(event) => handleTermsAndConditionsModal(event)}
              onKeyPress={handleKeyPress}>
              {renderContent(
                Dictionary.airPaymentFooterCheckboxLinkLabel.key,
                Dictionary.airPaymentFooterCheckboxLinkLabel.defaultValue,
              )}
            </span>
          </Typography>
        </div>
        {showLineError && (
          <div className={classes.errorMessage}>
            <div className={classes.checkboxIconSize}>
              <WarningIcon width={24} height={24} className={classes.checkboxIconSize} />
            </div>
            <p>{lineErrorMessage}</p>
          </div>
        )}
      </div>
    </>
  );

  const isProcessingPayment =
    fullPaymentLoading || bookFlightLoading || bookFlightAuthorizationLoading;

  const loaderStatus = ldLoader
    ? getDictionaryValue(Dictionary.loaderRetrievePaymentDetails.key)
    : isProcessingPayment && getDictionaryValue(Dictionary.loaderCompletingReservation.key);

  return (
    <>
      <Helmet>
        <title>{getDictionaryValue(Dictionary.airPaymentFlightsSeoTitle.key)}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <royal-payway-three-ds
        env={REACT_APP_AIR_3DS_ENV}
        payment-channel="RW"
        ref={royalPayway}
        data-testid="royal-payway"
      />
      <ItineraryDetails />
      <AirLoader
        open={ldLoader || isGiftcardLoading || isProcessingPayment}
        status={loaderStatus}
      />
      <div className={classes.progressLineContainer}>
        <ProgressLineItems selectedFlightType={flightTypeCodeFromSession} />
      </div>
      <Grid container className={classes.gridContainer}>
        {(displayMobileBanner || displayDesktopBanner) && getBannerElement()}
        <Grid
          item
          xs={12}
          className={classes.paymentContainer}
          style={{ marginTop: displayMobileBanner ? '30px' : '0px' }}>
          <ReviewCard
            cardTitle={
              isChangeFlights
                ? renderContent(
                    Dictionary.airFlightSummaryNewFlightsLabel.key,
                    Dictionary.airFlightSummaryNewFlightsLabel.defaultValue,
                  )
                : renderContent(
                    Dictionary.airFlightSummaryTitle.key,
                    Dictionary.airFlightSummaryTitle.defaultValue,
                  )
            }
            title={renderContent(
              Dictionary.airCheckoutPageTitle.key,
              Dictionary.airCheckoutPageTitle.defaultValue,
            )}
            data={holdFlight}
            extraData={extraDataPayment}
            isChangeFlights={isChangeFlights}
            isSummary
            summaryBalance
            isDesktop={expandReviewCard}
            handleAccordion={() => setExpandReviewCard(!expandReviewCard)}
            balanceLabel={isChangeFlights}
            setTotalBalance={setTotalBalance}
            setIsRefund={setIsRefund}
            displayDesktopBanner={displayDesktopBanner}
            isOneWay={isOneWay}
          />
          <PassengersDetailsWizard
            passengerDetailsFormRef={passengerDetailsFormRef}
            setShouldDisableCta={setpassengerInfoComplete}
          />
          {totalBalance > 0 && (
            <PageSection className={classes.cardHeaderCheckoutPayment}>
              {ldAirFlagsGiftCard > 0 && currency === 'USD' && (
                <GiftCards
                  setRemainingBalance={setRemainingBalance}
                  holdFlightData={holdFlight}
                  extraData={extraDataPayment}
                  showErrorGiftCardsBanner={showErrorGiftCardsBanner}
                  hideErrorBanners={hideErrorBanners}
                  setLoader={setIsGiftcardLoading}
                  setGiftCard={setGiftCard}
                  currency={currency}
                  setDisableCreditCardForm={setDisableCreditCardForm}
                  maximumNumberOfGiftCards={ldAirFlagsGiftCard}
                />
              )}
              {isValidBoolean(ldAirFlagsUnifiedPayment) && (
                <CreditCardInformation
                  paymentMethodFormRef={paymentMethodFormRef}
                  remainingBalance={remainingBalance}
                  holdFlightData={holdFlight}
                  extraData={isChangeFlights ? newExtraDataPayment : extraDataPayment}
                  bookingProfile={bookingProfile}
                  showPaymentErrorBanner={showPaymentErrorBanner}
                  showCancelPaywayBanner={showCancelPaywayBanner}
                  showErrorvalidationBanner={showErrorvalidationBanner}
                  payment3dsToggle={ldAirFlagsThreeDS}
                  paymentSelection={paymentSelect}
                  handleOnChangeCTA={handleOnChangeCTA}
                  setCreditCard={setCreditCard}
                  disableForm={disableCreditCardForm}
                  giftCards={giftCards}
                  bookFlightPayment={bookFlightPayment}
                  setSubmitPaymentRef={setSubmitPaymentRef}
                  heldConfirmInputInformation={heldConfirmInputInformation}
                  royalPayway={royalPayway}
                  onPropertyChage={handleOnChangePayment}
                  isRefund={isRefund}
                  totalBalance={totalBalance}
                  showLineError={showLineError}
                  unifiedPaymentEnabled={ldAirFlagsUnifiedPayment}
                  setPayLaterFromAmend={setPayLaterFromAmend}
                  payLaterFromAmend={payLaterFromAmend}
                  onFinishUpcPayment={() => setFullPaymentLoading(false)}
                  loading={ldLoader}
                />
              )}
            </PageSection>
          )}
          <TermsAndConditionsModal
            openModal={openTermsAndConditionsModal}
            handleClose={handleCloseTermsAndConditionsModal}
          />
        </Grid>
        <ExtendSessionModal />
        <AirFooter className={classes.airFooter} />
      </Grid>
      {ldAirFlagsNewPayment && (
        <div className={`${classes.stickyMainFooter} ${showLineError ? 'errorBorder' : ''}`}>
          <PaymentFooter
            showTermsAndConditionsCheckBox
            showTooltip={isRefund && totalBalance !== 0}
            tooltipContent={tooltipContent}
            tooltipLabel={tooltipLabel}
            className={classes.paymentFooter}
            onChangeCheckBox={(e) => checkboxHandler(e)}
            disableSbumit={false}
            paymentSummaryLabel={paymentSummaryLabel}
            checkboxLabel={checkboxLabel}
            submitCTA={renderContent(
              Dictionary.airPaymentFooterSummaryButtonLabel.key,
              Dictionary.airPaymentFooterSummaryButtonLabel.defaultValue,
            )}
            submitHandler={handleSubmitPayment}
          />
        </div>
      )}
    </>
  );
};

export default Payment;
